
import LinkCTA from '2-core-elements/LinkCTA/LinkCTA';
import RadioButton from '5-react-form-apps/FormElements/RadioButton';
import { createContext, type FC, type ReactElement, type PropsWithChildren, type ReactNode, useContext, useRef, useState, useEffect } from 'react';
import getRandomString from 'utils/getRandomString';

interface RadioSelectContextProps {
    name: string;
    selected: string;
    onChange: (value: string) => void;
}

type RadioGroupProps = {
    groupTitle?: string;
    name?: string;
    value: string;
    onChange: (value: string) => void;
    children: ReactNode;
}

const RadioSelectContext = createContext<RadioSelectContextProps | null>(null);

const RadioSelect = ({ groupTitle, name, value, onChange, children }: RadioGroupProps): JSX.Element => {
    const groupNameRef = useRef(name || getRandomString());
    const groupName = groupNameRef.current;

    return (
        <RadioSelectContext.Provider value={{ name: groupName, selected: value, onChange }}>
            <fieldset className="radio-select-wrapper">
                {groupTitle && (
                    <legend>{groupTitle}<span className="required">*</span></legend>
                )}

                <RadioButton>
                    {children}
                </RadioButton>
            </fieldset>
        </RadioSelectContext.Provider>
    );
};

interface RadioSelectItemProps {
    value: string;
    label: string;
    message?: string | ReactElement;
    isDisabled?: boolean;
    suffixText?: string;
}

const RadioSelectItem: FC<RadioSelectItemProps> = ({ value, label, message, isDisabled, suffixText }) => {
    const ctx = useContext(RadioSelectContext);
    if (!ctx) {
        throw new Error('RadioSelect.Item must be used inside RadioSelect');
    }

    const { name, selected, onChange } = ctx;

    const isSelected = selected === value;

    return (
        <div className={`radio-select-item-wrapper${isSelected ? ' is-selected' : ''}${isDisabled ? ' is-disabled' : ''}`}>
            <div className="radio-select-item">
                {/*
                    // the HTML structure of RadioButtonItem couldn't accommodate
                    // the functionallity we need in this component.
                    // that's why we create new component for the radio item.
                */}
                <label>
                    <input
                        type="radio"
                        name={name}
                        id={`${name}-${value}`}
                        value={value}
                        checked={isSelected}
                        disabled={isDisabled}
                        onChange={(event): void => {
                            onChange(event.target.value);
                        }}
                    />
                    <span>{label}</span>
                    <span className="suffix-text">{suffixText || ''}</span>
                </label>
            </div>
            {message && (
                <div className="radio-item-message">
                    {message}
                </div>
            )}
        </div>
    );
};

interface RadioSelectItemWithContentProps extends PropsWithChildren{
    value: string;
    label: string;
    suffixText?: string;
    isDisabled?: boolean;
}

const RadioSelectItemWithContent: FC<RadioSelectItemWithContentProps> = ({ value, label, suffixText, isDisabled, children }) => {
    const ctx = useContext(RadioSelectContext);
    if (!ctx) {
        throw new Error('RadioSelect.ItemWithContent must be used inside RadioSelect');
    }

    const { name, selected, onChange } = ctx;
    const [isExpandable, setIsExpandable] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const contentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (contentRef.current) {
            const collapsedHeight = 40;
            setIsExpandable(contentRef.current.scrollHeight > collapsedHeight);
        }
    }, [children]);


    const isSelected = selected === value;

    const toggleExpanded = (): void => {
        setIsExpanded(!isExpanded);
    };

    return (
        <div className={`radio-select-item-wrapper${isSelected ? ' is-selected' : ''}${isDisabled ? ' is-disabled' : ''}`}>
            <div className="radio-select-content-layout">
                <div className="radio-select-input-wrapper">
                    <input
                        type="radio"
                        name={name}
                        id={`${name}-${value}`}
                        value={value}
                        checked={isSelected}
                        disabled={isDisabled}
                        onChange={(event): void => {
                            onChange(event.target.value);
                        }}
                        aria-label={`${label} ${suffixText || ''}`}
                    />
                </div>
                <div className={`radio-select-content-wrapper${isExpandable ? ' is-expandable' : ''}`}>
                    <label htmlFor={`${name}-${value}`}>
                        <span className="label-text">{label}</span>
                        {suffixText && <span className="label-suffix">{suffixText}</span>}
                        {children && (
                            <div className={`radio-select-content-box`}>
                                <div
                                    ref={contentRef}
                                    className={`expandable-content-box${isExpanded ? ' is-expanded' : ''}`}
                                >
                                    {children}
                                </div>
                            </div>
                        )}
                    </label>
                    <div className="expandable-button-wrapper" aria-hidden={!isExpandable}>
                        <LinkCTA
                            suffixIcon={isExpanded ? 'minus' : 'add'}
                            onClick={toggleExpanded}
                            size={`xs`}
                        >
                            {isExpanded ? 'Hide Detail' : 'Show Detail'}
                        </LinkCTA>
                    </div>
                </div>
            </div>
        </div>
    );
};


RadioSelect.Item = RadioSelectItem;
RadioSelect.ItemWithContent = RadioSelectItemWithContent;

export default RadioSelect;
